import { useState, useMemo, useCallback, useEffect } from 'react'
import { SellModelType } from '../types'
import { permissionCheck } from '../../../../../../../../baseComponents/PermissionChecker/function'
import ITHeaderText, { ITHeaderTextCustomer } from '../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderText'
import InputTableLeftColumnForm from '../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/InputTableLeftColumnForm'
import { InputTableLeftTabsProps, InputTableLeftColumnChange } from '../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/types'
import { OrderHeaderURLs, SetCompleteDataTy } from '../../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { CustomSelectWithSearchBar } from '../../../../../../../../forms/CustomSelect'
import { FormElement, FormRender } from '../../../../../../../../forms/FormRenderer/types'
import ModalEditOrderCurrency from '../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderTable/TableComponents/ModalsOrderTable/ModalEditOrderCurrency'
import { parseAddressToOneLine } from '../../../../../../../../utils/globals/address'
import { twinFetchPostJSON } from '../../../../../../../../utils/globals/data'
import { TwinDictionary } from '../../../../../../../../utils/globals/dictionary'
import { notHaveCountersWorkingCenter } from '../../../../../../../../utils/globals/order/order'
import { returnClassOfSpanTemplate } from '../../../../../../../../utils/globals/tailwind'
import { UseEditDeleteModalProps } from '../../../../../../../../utils/hooks/useEditDeleteModal'
import useTwinTranslation from '../../../../../../../../utils/hooks/useTwinTranslation'
import { WorkingCenterPayload } from '../../../../../../../../utils/reducers/company/workingCenters'
import { getActiveCurrenciesDictionary, getUserPermissions } from '../../../../../../../../utils/reducers/getters'
import { SellTagsPayload } from '../../../../../../../../utils/reducers/sells/sellTags'
import { sortEmployeesByUser } from '../../../../../../../../utils/globals/employees'
import { EmployeesPayload } from '../../../../../../../../utils/reducers/company/employees'
import { ITHeaderOrderTags } from '../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components'
import '../sellHeader.sass'
import ModalEditOrderWorkingCenter from '../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderTable/TableComponents/ModalsOrderTable/ModalEditOrderWorkingCenter'
import { CustomSelectBlockedItemsSearchbar } from '../../../../../../../../forms/CustomSelect/CustomSelectBlockedItems'
import { CustomSellSelectCampaign, CustomSellSelectProject } from '../../../../../../../../specificComponents/sells/components'
import { InputWithLabelMargin } from '../../../../../../../../forms/Input'


type SellStepHeaderMainProps = InputTableLeftTabsProps & SellStepHeaderMainLogicProps & {
    orderHeaderURLs: OrderHeaderURLs
    updateCurrencyURL: string
    sellTags: SellTagsPayload
    employees: EmployeesPayload,
}

const SellStepHeaderMain: React.FC<SellStepHeaderMainProps> = ({ workingCenters, getCompleteData, handleChange, headerData, updateCurrencyURL, sellTags, employees, ...logic }) => {
    const { t } = useTwinTranslation()
    const { dictSellStepHeaderExtraData, activeCurrencies, openModal, setOpenModal, changeCurrencyId, dictWarehouses, workingCenterAddress, memorizedWorkingCenters, changeWorkingCenterId, getCustomerProjects, customerProjects, getCustomerCampaigns, customerCampaigns  } = useSellStepHeaderMainLogic({ headerData, workingCenters, handleChange, ...logic })
    const workingCenterSection: FormElement<keyof SellModelType>[] = []
    if (headerData?.QuoteOrderId || headerData?.SellOrderId || headerData?.SellDeliveryNoteOrderId) {
        workingCenterSection.push(
            {
                name: 'WorkingCenterId',
                component: 'Custom',
                render: ({ value }) => <ITHeaderText label={t('soldBy', 'Vendido por')} value={headerData?.WorkingCenter?.name || ''} className={returnClassOfSpanTemplate(3)} />
            },
        )
    } else {
        workingCenterSection.push(
             {
                name: 'WorkingCenterId',
                component: 'Custom',
                render: ({ extraProps }) => {
                    return <CustomSelectBlockedItemsSearchbar label={t('soldBy', 'Vendido por')} items={extraProps.memorizedWorkingCenters} value={extraProps.WorkingCenterId} onChange={extraProps.changeWorkingCenterId} className={returnClassOfSpanTemplate(3)} isStateLess={true} blockItemsFnc={extraProps.notHaveCountersWorkingCenter} sortItems={true} />
                },
                extraProps: {
                    memorizedWorkingCenters,
                    WorkingCenterId: headerData.WorkingCenterId,
                    changeWorkingCenterId,
                    notHaveCountersWorkingCenter
                }
            },
        )
    }
    const sections: FormRender<SellModelType> = [
        {
            cols: 5,
            elements: [
                {
                    name: 'CustomerId',
                    component: 'Custom',
                    render: () => <ITHeaderTextCustomer label={t('customer', 'Cliente')} headerData={headerData} className={returnClassOfSpanTemplate(5)} showCustomerProjectName={false} />
                },
                {
                    name: 'custom_name',
                    component: 'Custom',
                    render: (props) => {
                        return <InputWithLabelMargin name='custom_name' label={t('customName', 'Nombre personalizado')} onChange={props.onChange} defaultValue={headerData.custom_name} className={returnClassOfSpanTemplate(5)}/>
                    }
                },
                {
                    name: 'CustomerProjectId',
                    component: 'Custom',
                    render: ({ extraProps, setLockOnClickOut }) => <CustomSellSelectProject getCompleteData={getCustomerProjects} handleChange={handleChange} headerData={headerData} items={extraProps.customerProjects} classNameField={returnClassOfSpanTemplate(5)} setLockOnClickOut={setLockOnClickOut} />,
                    extraProps: {
                        customerProjects: customerProjects || {}
                    }
                },
                {
                    name: 'CampaignCustomerId',
                    component: 'Custom',
                    render: ({ extraProps, setLockOnClickOut }) => <CustomSellSelectCampaign getCompleteData={getCustomerCampaigns} handleChange={handleChange} headerData={headerData} items={extraProps.customerCampaigns} label={t('campaign', 'Campaña')} classNameField={returnClassOfSpanTemplate(5) + '-mt-15 -mb-8'} setLockOnClickOut={setLockOnClickOut} />,
                    extraProps: {
                        customerCampaigns: customerCampaigns || {}
                    }
                },

                {
                    name: 'RepresentativeEmployeeId',
                    component: 'CustomSelectWithSearchBar',
                    label: t('representative', 'Representante'),
                    items: employees || {},
                    onChange: getCompleteData,
                    className: returnClassOfSpanTemplate(5),
                    sortItems: true,
                    fieldName: 'fullname_short',
                    sortFunction: (emp) => sortEmployeesByUser(emp, false)
                },
                {
                    name: 'processed_date',
                    component: 'InputCalendarStateFull',
                    label: t('date', 'Fecha'),
                    onlyValids: true,
                    className: returnClassOfSpanTemplate(3),
                    required: true
                },
                {
                    name: 'full_assigned_number',
                    component: 'Custom',
                    render: ({ value } ) => <ITHeaderText label={t('draftN', 'Nº de borrador')} value={value} className={(headerData?.assigned_number ? '' : 'text-red-BA') + ' ' + returnClassOfSpanTemplate(2)} />,
                },
                ...workingCenterSection,
                {
                    name: 'BillingWorkingCenterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('billingBy', 'Facturado por')} value={workingCenters?.[headerData?.BillingWorkingCenterId]?.name || ''} className={returnClassOfSpanTemplate(3)} />,
                },
                {
                    name: 'WorkingCenterCounterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('serie', 'Serie')} value={headerData?.WorkingCenterCounter?.serie || ''} className={returnClassOfSpanTemplate(2)} />,
                },
                {
                    name: 'CustomerAddress',
                    component: 'Custom',
                    render: ({ value }) => {
                        return <ITHeaderText label={t('fiscalAddressCustomer', 'Dirección Fiscal cliente')} value={dictSellStepHeaderExtraData?.CustomerAddress || ''} className={returnClassOfSpanTemplate(5)} />
                    },
                    extraProps: {
                        dictSellStepHeaderExtraData
                    }
                },
                {
                    name: 'WorkingCenterAddressId',
                    component: 'Custom',
                    render: ({ value }) => {
                        return <ITHeaderText label={t('workingCenterFiscalAddress', 'Dirección Fiscal centro de trabajo')} value={workingCenterAddress || ''} className={returnClassOfSpanTemplate(5)} />
                    },
                    extraProps: {
                        workingCenterAddress
                    }
                },
                {
                    name: 'WarehouseId',
                    component: 'CustomSelectWithSearchBar',
                    label: t('warehouse', 'Almacén'),
                    items: dictWarehouses || {},
                    className: returnClassOfSpanTemplate(5),
                    value: headerData?.WarehouseId || 0
                },
                {
                    name: 'CurrencyId',
                    component: 'Custom',
                    render: ({ extraProps }) => {
                        return <CustomSelectWithSearchBar label={t('currency', 'Moneda')} items={extraProps.activeCurrencies} value={extraProps.CurrencyId} onChange={extraProps.changeCurrencyId} className={returnClassOfSpanTemplate(2)} isStateLess={true} />
                    },
                    extraProps: {
                        activeCurrencies,
                        CurrencyId: headerData.CurrencyId,
                        changeCurrencyId
                    }
                },
                {
                    name: 'OrderTags',
                    component: 'Custom',
                    render: ({ extraProps }) => {
                        return (<ITHeaderOrderTags selectedTags={extraProps.selectedTags} dictTags={sellTags || []} headerData={headerData} onChange={handleChange} {...logic} className={returnClassOfSpanTemplate(5)} />)
                    },
                    extraProps: {
                        selectedTags: headerData?.tags,
                    }
                },
            ]
        }
    ]
    return (
        <div className='flex flex-auto flex-col'>
            <InputTableLeftColumnForm form={sections} handleChange={handleChange} headerData={headerData} />
            {openModal?.type === 'edit' && openModal?.allRowData?.CurrencyId ? 
                <ModalEditOrderCurrency Currency={activeCurrencies?.[openModal?.allRowData?.CurrencyId]?.name} id={headerData?.id} {...{ updateCurrencyURL, onSubmit: getCompleteData, setOpened: () => setOpenModal(null), allRowData: openModal?.allRowData}}/>
                : null
            }
            {openModal?.type === 'edit' && openModal?.allRowData?.WorkingCenterId ? 
                <ModalEditOrderWorkingCenter id={headerData?.id} {...{ updateWorkingCenterURL: logic.orderHeaderURLs.updateWorkingCenter, onSubmit: getCompleteData, setOpened: () => setOpenModal(null), allRowData: openModal?.allRowData}}/>
                : null
            }
        </div>
    )
}

interface SellStepHeaderMainLogicProps {
    headerData: TwinDictionary
    workingCenters: WorkingCenterPayload
    setCompleteData: SetCompleteDataTy
    handleChange: InputTableLeftColumnChange
}

interface SellStepHeaderExtraData {
    CustomerAddress: string | null
    CustomerProjects: TwinDictionary
}

export const useSellStepHeaderMainLogic = ({ workingCenters, headerData }: SellStepHeaderMainLogicProps) => {
    const { t } = useTwinTranslation()
    const [openModal, setOpenModal] = useState<UseEditDeleteModalProps>(null)
    const CustomerAddress = headerData.CustomerAddress
    const WorkingCenterAddress = headerData.WorkingCenterAddress
    const WorkingCenterId = headerData.WorkingCenterId
    const CustomerId = headerData.CustomerId
    const CurrencyId = headerData.CurrencyId
    const [customerProjects, setCustomerProjects] = useState<TwinDictionary[]>([{ [headerData?.CustomerProject?.id]: headerData?.CustomerProject }])
    const [customerCampaigns, setCustomerCampaigns] = useState<TwinDictionary[]>([{ [headerData?.CampaignCustomer?.id]: headerData?.CampaignCustomer?.Campaign }])

    const [dictSellStepHeaderExtraData, setDictSellStepHeaderExtraData] = useState<SellStepHeaderExtraData>({ CustomerAddress: CustomerAddress ? parseAddressToOneLine(CustomerAddress) : null, CustomerProjects: { [headerData?.CustomerProject?.id]: headerData?.CustomerProject } })
    const activeCurrencies = getActiveCurrenciesDictionary()
    const workingCenterAddress = useMemo(() => parseAddressToOneLine(WorkingCenterAddress), [WorkingCenterAddress])
    const anyWarehouseText =  t('anyWarehouse', 'Cualquier almacén')
    const dictWarehouses = useMemo(() => ({
        '0': { id: '0', name: anyWarehouseText },
        ...workingCenters?.[parseInt(WorkingCenterId || '1')]?.dictWarehouses
    }), [workingCenters, WorkingCenterId, anyWarehouseText])
    const memorizedWorkingCenters = useMemo(() => {
        const copyWorkingCenters = JSON.parse(JSON.stringify(workingCenters))
        if (headerData.WorkingCenterId && headerData.WorkingCenter && !copyWorkingCenters[headerData.WorkingCenterId]) {
            copyWorkingCenters[headerData.WorkingCenterId] = { id: headerData.WorkingCenterId, name: headerData.WorkingCenter?.name }
        }
        return copyWorkingCenters
    }, [headerData?.WorkingCenterId, headerData?.WorkingCenter, workingCenters])

    const getAllCustomerAddress = useCallback(async () => {
        const items = await twinFetchPostJSON('/api/app/customer/customerAddress/getAllCustomerAddresses', { where: { 'CustomerId': CustomerId, 'fiscal_address': true } })
        if (items && items.length) {
            setDictSellStepHeaderExtraData((old) => {
                const copy = JSON.parse(JSON.stringify(old))
                return {
                    ...copy,
                    CustomerAddress: parseAddressToOneLine(items[0])
                }
            })
        }
    }, [setDictSellStepHeaderExtraData, CustomerId])
    const changeCurrencyId = useCallback((value: string) => {
        if (value !== CurrencyId){
            setOpenModal({ type: 'edit', allRowData: { CurrencyId: value } })
        }
    }, [setOpenModal, CurrencyId])
    
    const changeWorkingCenterId = useCallback((value: string) => {
        if (value !== CurrencyId){
            setOpenModal({ type: 'edit', allRowData: { WorkingCenterId: value } })
        }
    }, [setOpenModal, CurrencyId])

    const getCustomerProjects = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/customer/customerProject/getAllCustomerProjects', {
            where:
            {
                'CustomerProject.active': true,
                CustomerId
            },
        })
        if (result) {
            const projects: TwinDictionary[] = []
            for (const element of result) {
                projects.push({ ...element, value: element.id, text: element?.name })
            }
            setCustomerProjects(projects)
        }
    }, [CustomerId])
    const getCustomerCampaigns = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/customer/campaign/customer/getAllCampaignCustomers', {
            where:
            {
                CustomerId
            },
        })
        if (result) {
            const campaigns: TwinDictionary[] = []
            for (const element of result) {
                campaigns.push({ ...element?.Campaign, id: element?.id, value: element.id, text: element?.Campaign?.name })
            }
            setCustomerCampaigns(campaigns)
        }
    }, [CustomerId])


    useEffect(() => {
        if (!CustomerAddress?.id && permissionCheck(getUserPermissions('customer.profile.address'), 'read')) {
            getAllCustomerAddress()
        }
    }, [getAllCustomerAddress, CustomerAddress?.id])

    useEffect(() => {
        if (permissionCheck(getUserPermissions('customer.projects.main'), 'read') || permissionCheck(getUserPermissions('customer.profile.projects'), 'read')) {
            getCustomerProjects()
        }
    }, [getCustomerProjects])

    useEffect(() => {
        if (permissionCheck(getUserPermissions('customer.campaigns.main'), 'read') || permissionCheck(getUserPermissions('customer.profile.campaign'), 'read') || permissionCheck(getUserPermissions('customer.campaigns.inner'), 'read')) {
            getCustomerCampaigns()
        }
    }, [getCustomerCampaigns])

    return { getAllCustomerAddress, dictSellStepHeaderExtraData, activeCurrencies, openModal, setOpenModal, changeCurrencyId, changeWorkingCenterId, workingCenterAddress, memorizedWorkingCenters, getCustomerCampaigns, getCustomerProjects, customerProjects, customerCampaigns, dictWarehouses }

}

export default SellStepHeaderMain